const NS = 'subscription-swap';

const BILLING_FREQUENCY_ID = `${NS}__billing-frequency`;
const BILLING_FREQUENCY_IDS = {
  monthly: `${NS}__billing-monthly`,
  quarterly: `${NS}__billing-quarterly`,
  biannually: `${NS}__billing-biannually`,
  annually: `${NS}__billing-annually`,
  unknown: `${NS}__billing-unknown`,
};

const DISABLED_BILLING_FREQUENCY_IDS = {
  monthly: [],
  quarterly: [],
  biannually: [BILLING_FREQUENCY_IDS.monthly, BILLING_FREQUENCY_IDS.quarterly],
  annually: [BILLING_FREQUENCY_IDS.monthly, BILLING_FREQUENCY_IDS.quarterly, BILLING_FREQUENCY_IDS.biannually],
  unknown: [
    BILLING_FREQUENCY_IDS.monthly,
    BILLING_FREQUENCY_IDS.quarterly,
    BILLING_FREQUENCY_IDS.biannually,
    BILLING_FREQUENCY_IDS.annually,
  ],
};

const DELIVERY_FREQUENCY_ID = `${NS}__delivery-frequency`;
const DELIVERY_FREQUENCY_IDS = {
  monthly: `${NS}__delivery-monthly`,
  quarterly: `${NS}__delivery-quarterly`,
  unknown: `${NS}__delivery-unknown`,
};

const DISABLED_DELIVERY_FREQUENCY_IDS_BY_BILLING = {
  monthly: {
    monthly: [],
    quarterly: [],
    unknown: [DELIVERY_FREQUENCY_IDS.monthly, DELIVERY_FREQUENCY_IDS.quarterly],
  },
  quarterly: {
    monthly: [],
    quarterly: [],
    unknown: [DELIVERY_FREQUENCY_IDS.monthly, DELIVERY_FREQUENCY_IDS.quarterly],
  },
  biannually: {
    monthly: [],
    quarterly: [],
    unknown: [DELIVERY_FREQUENCY_IDS.monthly, DELIVERY_FREQUENCY_IDS.quarterly],
  },
  annually: {
    monthly: [],
    quarterly: [],
    unknown: [DELIVERY_FREQUENCY_IDS.monthly, DELIVERY_FREQUENCY_IDS.quarterly],
  },
  unknown: {
    monthly: [DELIVERY_FREQUENCY_IDS.monthly, DELIVERY_FREQUENCY_IDS.quarterly],
    quarterly: [DELIVERY_FREQUENCY_IDS.monthly, DELIVERY_FREQUENCY_IDS.quarterly],
    unknown: [DELIVERY_FREQUENCY_IDS.monthly, DELIVERY_FREQUENCY_IDS.quarterly],
  },
};

const FREQUENCY_NAMES = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  biannually: 'Bi-Annual',
  annually: 'Annual',
};

const FREQUENCY_MONTHS = {
  monthly: 1,
  quarterly: 3,
  biannually: 6,
  annually: 12,
};

const OLD_SUBSCRIPTION_INPUT_ID = `${NS}__old-subscription`;
const EMPTY_CLUB_ID = `${NS}__old-subscription--empty`;
const SWAP_CLUBS_INPUT_ID = `${NS}__swap-clubs`;
const NEW_CLUB_INPUT_ID = `${NS}__new-club`;

const RADIO_WRAPPER_CLASS = `${NS}__radio-wrapper`;
const FIELD_WRAPPER_CLASS = `${NS}__field-wrapper`;

const BILLING_DATE_CLASS = `${NS}__billing-date-token`;
const BILLING_DATE_MESSAGE_CLASS = `${NS}__message-wrapper--frequency`;

const DELIVERY_DATE_CLASS = `${NS}__delivery-date-token`;
const DELIVERY_DATE_MESSAGE_CLASS = `${NS}__message-wrapper--club-swap`;

const ERROR_MESSAGE_CLASS = `${NS}__message-wrapper--error`;
const SUCCESS_MESSAGE_CLASS = `${NS}__message-wrapper--success`;

const SUBMIT_BUTTON_ID = `${NS}__submit`;

const DATE_FORMAT = { month: 'long', day: 'numeric', year: 'numeric' };

const PRICE_WRAPPER_CLASS = `${NS}__pricing-wrapper`;
const PRICE_VALUE_CLASS = `${NS}__pricing-value`;
const CURRENT_PRICE_WRAPPER_CLASS = `${PRICE_WRAPPER_CLASS}--current`;
const UPDATED_PRICE_WRAPPER_CLASS = `${PRICE_WRAPPER_CLASS}--updated`;

const DEFAULT_PRICING = {
  monthly: {
    monthly: -1,
    quarterly: -1,
  },
  quarterly: {
    monthly: -1,
    quarterly: -1,
  },
  biannually: {
    monthly: -1,
    quarterly: -1,
  },
  annually: {
    monthly: -1,
    quarterly: -1,
  },
};

const DEFAULT_ERROR_MESSAGE = `Unfortunately, there was an error while processing your request. Please try again later or <a href="/pages/contact" class="font-body-3">reach out for help</a>.`;

class SubscriptionSwap extends HTMLElement {
  activeSubscriptions = [];
  pricing = {};
  collectionHandle = '';
  selectedSubscriptionIdToModify = '';
  selectedVariantIdForSwap = '';
  customerId = '';
  selectedDeliveryFrequency = '';
  selectedBillingFrequency = '';
  countryCode = '';

  get selectedSubscriptionToModify() {
    const subscription = this.activeSubscriptions.find(
      (x) => x.subscriptionContractId.toString() === this.selectedSubscriptionIdToModify
    );
    if (subscription) {
      return subscription;
    }
    return null;
  }

  get didSwapClubs() {
    const selectedSubscription = this.selectedSubscriptionToModify;
    if (!selectedSubscription) {
      return false;
    }
    const contractDetails = JSON.parse(selectedSubscription.contractDetailsJSON)[0];
    return contractDetails.variantId.split('/').pop() !== this.selectedVariantIdForSwap;
  }

  get didChangeFrequency() {
    const selectedSubscription = this.selectedSubscriptionToModify;
    if (!selectedSubscription) {
      return false;
    }
    return (
      this.selectedBillingFrequency !== this.getFrequencyForSubscription(selectedSubscription, 'billing') ||
      this.selectedDeliveryFrequency !== this.getFrequencyForSubscription(selectedSubscription, 'delivery')
    );
  }

  get nextDeliveryDateForCurrentSubscription() {
    const selectedSubscription = this.selectedSubscriptionToModify;
    if (!selectedSubscription) {
      const now = new Date();
      if (now.getDate() >= 15) {
        const nextMonth = new Date();
        nextMonth.setDate(15);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        return nextMonth;
      }
      const thisMonth = new Date();
      thisMonth.setDate(15);
      return thisMonth;
    }
    const deliveryDate = new Date(selectedSubscription.activatedOn);
    deliveryDate.setDate(15);
    while (deliveryDate < new Date()) {
      deliveryDate.setMonth(deliveryDate.getMonth() + selectedSubscription.deliveryPolicyIntervalCount);
    }
    return deliveryDate;
  }

  constructor() {
    super();
    this.getData();
    this.setupListeners();
    this.fetchActiveSubscriptions();
  }

  titleCase(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Get liquid data from component's dataset
   */
  getData() {
    this.customerId = this.dataset.customerId;
    this.countryCode = this.dataset.countryCode;
    this.pricing = Object.keys(this.dataset)
      .filter((key) => key.startsWith('priceAdjusted'))
      .reduce((obj, key) => ({ ...obj, [key]: this.dataset[key] }), {});
  }

  /**
   * Builds the key to lookup the price of a selling plan for a variant
   * @param {string} billingFrequency The selected billing frequency
   * @param {string} deliveryFrequency The selected delivery frequency
   * @param {string} variantId The selected variant ID
   * @returns The key of the price in the pricing object
   */
  getPricingKey(billingFrequency, deliveryFrequency, variantId) {
    return `priceAdjusted${this.titleCase(deliveryFrequency)}Delivery${this.titleCase(
      billingFrequency
    )}Billing-${variantId}`;
  }

  /**
   * Gets the price of a selling plan for a variant
   * @param {string} billingFrequency The selected billing frequency
   * @param {string} deliveryFrequency The selected delivery frequency
   * @param {string} variantId The selected variant ID
   * @returns The price if found, otherwise -1
   */
  getPrice(billingFrequency, deliveryFrequency, variantId) {
    const key = this.getPricingKey(billingFrequency, deliveryFrequency, variantId);
    if (key in this.dataset) {
      return this.dataset[key];
    }

    return -1;
  }

  /**
   * Formats a price as a string. If international, it adds "USD" to the end.
   * @param {number} price The price to be formatted
   * @returns The formatted price
   */
  formatPrice(price) {
    const p = (price / 100).toFixed(2);
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    const str = formatter.format(p);
    if (this.countryCode !== 'US') {
      str.concat(' USD');
    }
    return str;
  }

  /**
   * Add event listeners to the component
   */
  setupListeners() {
    const oldSubscriptionInput = this.querySelector(`#${OLD_SUBSCRIPTION_INPUT_ID}`);
    oldSubscriptionInput.addEventListener('change', this.handleOldSubscriptionChange.bind(this));
    const swapClubInput = this.querySelector(`#${SWAP_CLUBS_INPUT_ID}`);
    swapClubInput.addEventListener('change', this.handleSwapClubChange.bind(this));
    const newClubInput = this.querySelector(`#${NEW_CLUB_INPUT_ID}`);
    newClubInput.addEventListener('change', this.handleNewClubChange.bind(this));
    const deliveryFrequencyInput = this.querySelector(`#${DELIVERY_FREQUENCY_ID}`);
    deliveryFrequencyInput.addEventListener('change', this.handleDeliveryFrequencyChange.bind(this));
    const billingFrequencyInput = this.querySelector(`#${BILLING_FREQUENCY_ID}`);
    billingFrequencyInput.addEventListener('change', this.handleBillingFrequencyChange.bind(this));
    const submitButton = this.querySelector(`#${SUBMIT_BUTTON_ID}`);
    submitButton.addEventListener('click', this.handleSubmit.bind(this));
  }

  /**
   * Lookup customer's active subscription contracts via app lambda proxy to Appstle API.
   */
  async fetchActiveSubscriptions() {
    if (!this.customerId) {
      return;
    }
    try {
      const res = await fetch('/apps/customer-actions/get_subscription_contracts', {
        method: 'POST',
        body: JSON.stringify({ customerId: this.customerId }),
      });
      const data = await res.json();
      this.activeSubscriptions = data.data;
      this.renderActiveClubs();
      this.hideLoadingClub();

      const firstNonGiftSubscription = this.activeSubscriptions.find(
        (x) => !JSON.parse(x.contractDetailsJSON)[0].sellingPlanName.includes('Gift Subscription')
      );

      if (firstNonGiftSubscription) {
        const firstInput = this.querySelector(
          `#${OLD_SUBSCRIPTION_INPUT_ID}--${firstNonGiftSubscription.subscriptionContractId}`
        );
        firstInput.checked = true;
        firstInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Swaps the club on a subscription contract.
   * Since I was running into API Gateway timeouts, this is split into two calls.
   * The first call updates the contract and contact, while the second updates any scheduled fulfillment orders.
   * @param {string} contractId The ID of the Subscription Contract
   * @param {string} variantId The ID of the variant to swap to
   * @returns true if successful; otherwise false
   */
  async swapClubOnContract(contractId, variantId) {
    if (!this.customerId || !contractId || !variantId) {
      return false;
    }
    try {
      const res = await fetch('/apps/customer-actions/swap_subscription_product', {
        method: 'POST',
        body: JSON.stringify({ customerId: this.customerId, contractId, variantId }),
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.message === 'DONE') {
        this.toggleSuccessMessage(true);
        return true;
      } else if (data.message === 'CONTINUE') {
        const scheduledRes = await fetch('/apps/customer-actions/swap_scheduled_subscription_product', {
          method: 'POST',
          body: JSON.stringify({ customerId: this.customerId, contractResponse: data.data, variantId }),
          headers: { 'Content-Type': 'application/json' },
        });
        const scheduledData = await scheduledRes.json();
        if (scheduledData.message !== 'OK') {
          console.error(scheduledData);
          throw new Error('Error swapping club');
        } else {
          this.toggleSuccessMessage(true);
          return true;
        }
      } else {
        console.error(data);
        throw new Error('Error swapping club');
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * Updates the billing and delivery frequencies on a subscription contract.
   * @param {string} contractId The ID of the Subscription Contract
   * @param {number} billingMonths The number of months between billing
   * @param {number} deliveryMonths The number of months between deliveries
   * @returns true if successful; otherwise false
   */
  async updateContractFrequency(contractId, billingMonths, deliveryMonths) {
    if (!contractId || !billingMonths || !deliveryMonths) {
      return false;
    }
    try {
      const res = await fetch('/apps/customer-actions/update_subscription_frequency', {
        method: 'POST',
        body: JSON.stringify({ contractId: contractId.toString(), billingMonths, deliveryMonths }),
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.message !== 'OK') {
        console.error(data);
        throw new Error('Error updating frequency');
      } else {
        this.toggleSuccessMessage(true);
        return true;
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * Renders a list of active clubs for the customer
   */
  renderActiveClubs() {
    if (this.activeSubscriptions.length === 0) {
      this.setEmptyState();
      return;
    }
    const parent = this.querySelector(`#${OLD_SUBSCRIPTION_INPUT_ID} .${RADIO_WRAPPER_CLASS}`);
    this.activeSubscriptions.forEach((subscription) => {
      const { inputEl, labelEl } = this.buildClubElements(subscription);
      parent.appendChild(inputEl);
      parent.appendChild(labelEl);
    });
  }

  /**
   * Hide the skeleton loader for the active club selector
   */
  hideLoadingClub() {
    this.querySelectorAll(
      `#${OLD_SUBSCRIPTION_INPUT_ID}--loading, #${OLD_SUBSCRIPTION_INPUT_ID}--loading + label`
    ).forEach((el) => {
      el.remove();
    });
  }

  /**
   * Returns the name of the frequency for a subscription
   * @param {object} subscription The subscription contract
   * @param {'billing' | 'delivery'} type The frequency type to get
   * @returns
   */
  getFrequencyForSubscription(subscription, type) {
    const interval = subscription[`${type}PolicyInterval`];
    const intervalCount = subscription[`${type}PolicyIntervalCount`];

    if (interval.toLowerCase() === 'month') {
      if (intervalCount === 1) {
        return 'monthly';
      } else if (intervalCount === 3) {
        return 'quarterly';
      } else if (intervalCount === 6) {
        return 'biannually';
      } else if (intervalCount === 12) {
        return 'annually';
      }
    } else if (interval.toLowerCase() === 'year' && intervalCount === 1) {
      return 'annually';
    }
    return 'unknown';
  }

  handleOldSubscriptionChange(event) {
    this.selectedSubscriptionIdToModify = event.target.value;
    const subscription = this.selectedSubscriptionToModify;
    if (!subscription) {
      return;
    }

    this.updateToCurrentDeliveryFrequency(subscription);
    this.updateToCurrentBillingFrequency(subscription);
    this.updateToCurrentClubForSwap(subscription);
    this.updateBillingDateMessage(subscription);
    this.updatePrice(subscription.contractAmountUSD * 100, CURRENT_PRICE_WRAPPER_CLASS);
    this.updateDeliveryDateMessage();
    this.refreshPlanSelection();
  }

  handleSwapClubChange(event) {
    const newClubWrapper = this.querySelector(`.${FIELD_WRAPPER_CLASS}:has(#${NEW_CLUB_INPUT_ID})`);
    if (event.target.value === '1') {
      this.showHide(newClubWrapper, true);
    } else {
      this.showHide(newClubWrapper, false);
      this.updateToCurrentClubForSwap(this.selectedSubscriptionToModify);
    }
  }

  refreshPlanSelection() {
    this.toggleClubSwapMessage(this.didSwapClubs);
    this.toggleFrequencyChangeMessage(this.didChangeFrequency);
    this.updatePrice(
      this.getPrice(this.selectedBillingFrequency, this.selectedDeliveryFrequency, this.selectedVariantIdForSwap),
      UPDATED_PRICE_WRAPPER_CLASS
    );
    this.toggleUnavailablePlans();
    this.toggleSubmitButton();
  }

  handleNewClubChange(event) {
    this.selectedVariantIdForSwap = event.target.value;
    this.toggleNewClubSelections();
    this.refreshPlanSelection();
    this.updateDiscounts();
  }

  handleDeliveryFrequencyChange(event) {
    this.selectedDeliveryFrequency = event.target.value;
    this.refreshPlanSelection();
  }

  handleBillingFrequencyChange(event) {
    this.selectedBillingFrequency = event.target.value;
    this.refreshPlanSelection();
  }

  async handleSubmit(event) {
    event.preventDefault();

    const subscriptionToModify = this.selectedSubscriptionToModify;

    if (!subscriptionToModify || (!this.didChangeFrequency && !this.didSwapClubs)) {
      return;
    }

    event.target.disabled = true;
    event.target.classList.add('loading');

    try {
      const contractId = subscriptionToModify.subscriptionContractId;
      if (this.didSwapClubs) {
        const swapSuccessful = await this.swapClubOnContract(contractId.toString(), this.selectedVariantIdForSwap);
        if (!swapSuccessful) {
          throw new Error('Error swapping club');
        }
      }

      if (this.didChangeFrequency) {
        const successfulUpdate = await this.updateContractFrequency(
          contractId,
          FREQUENCY_MONTHS[this.selectedBillingFrequency],
          FREQUENCY_MONTHS[this.selectedDeliveryFrequency]
        );
        if (!successfulUpdate) {
          throw new Error('Error updating frequency');
        }
      }
    } catch {
      this.toggleErrorMessage(true);
    } finally {
      event.target.classList.remove('loading');
      event.target.disabled = false;
    }
  }

  /**
   * Sets the component to an empty state when no active subscriptions are found
   */
  setEmptyState() {
    this.querySelectorAll(
      `#${DELIVERY_FREQUENCY_ID} input, #${BILLING_FREQUENCY_ID} input, #${SWAP_CLUBS_INPUT_ID} input, #${NEW_CLUB_INPUT_ID} input`
    ).forEach((el) => {
      el.disabled = true;
    });

    this.querySelectorAll(`#${EMPTY_CLUB_ID}, #${EMPTY_CLUB_ID} + label`).forEach((el) => this.showHide(el, true));
  }

  /**
   * Update the club selected for swap to the given subscription's club
   * @param {object} subscription The selected subscription contract
   */
  updateToCurrentClubForSwap(subscription) {
    const contractDetails = JSON.parse(subscription.contractDetailsJSON)[0];
    const clubForSwap = this.querySelector(`#${NEW_CLUB_INPUT_ID}--${contractDetails.variantId.split('/').pop()}`);
    if (clubForSwap) {
      clubForSwap.checked = true;
      clubForSwap.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Updates the delivery frequency to the given subscription's frequency
   * @param {object} subscription The selected subscription contract
   */
  updateToCurrentDeliveryFrequency(subscription) {
    const currentFrequency = this.getFrequencyForSubscription(subscription, 'delivery');
    this.selectedDeliveryFrequency = currentFrequency;
    this.querySelectorAll(`#${Object.values(DELIVERY_FREQUENCY_IDS).join(', #')}`).forEach((el) => {
      el.checked = currentFrequency in DELIVERY_FREQUENCY_IDS && el.id === DELIVERY_FREQUENCY_IDS[currentFrequency];
    });
  }

  /**
   * Updates the billing frequency to the given subscription's frequency
   * @param {object} subscription The selected subscription contract
   */
  updateToCurrentBillingFrequency(subscription) {
    const currentFrequency = this.getFrequencyForSubscription(subscription, 'billing');
    this.selectedBillingFrequency = currentFrequency;
    this.querySelectorAll(`#${Object.values(BILLING_FREQUENCY_IDS).join(', #')}`).forEach((el) => {
      el.checked = currentFrequency in BILLING_FREQUENCY_IDS && el.id === BILLING_FREQUENCY_IDS[currentFrequency];
    });
  }

  /**
   * Updates the billing date message to the given subscription's next billing date
   * @param {object} subscription The selected subscription contract
   */
  updateBillingDateMessage(subscription) {
    if (!subscription) {
      return;
    }
    let nextBillingDate = new Date(subscription.nextBillingDate);
    if (
      this.getFrequencyForSubscription(subscription, 'delivery') === 'monthly' &&
      this.selectedDeliveryFrequency === 'quarterly'
    ) {
      nextBillingDate = new Date();
      nextBillingDate.setMonth(nextBillingDate.getMonth() + (nextBillingDate.getDate() < 15 ? 2 : 3));
      nextBillingDate.setDate(15);
    }
    document.querySelectorAll(`.${BILLING_DATE_CLASS}`).forEach((el) => {
      el.textContent = nextBillingDate.toLocaleString('default', DATE_FORMAT);
    });
  }

  /**
   * Updates the billing date message to the selected active subscription's next billing date
   * @param {object} subscription The selected subscription contract
   */
  updateDeliveryDateMessage() {
    const deliveryDate = this.nextDeliveryDateForCurrentSubscription;
    if (!deliveryDate) {
      return;
    }
    document.querySelectorAll(`.${DELIVERY_DATE_CLASS}`).forEach((el) => {
      el.textContent = deliveryDate.toLocaleString('default', DATE_FORMAT);
    });
  }

  /**
   * Updates a price element on the page
   * @param {number} price The price to be set
   * @param {string} wrapperClass The class of the price wrapper to update
   */
  updatePrice(price, wrapperClass) {
    const wrapperEl = this.querySelector(`.${wrapperClass}`);
    const valueEl = wrapperEl.querySelector(`.${PRICE_VALUE_CLASS}`);
    valueEl.textContent = this.formatPrice(price);

    if (wrapperClass === UPDATED_PRICE_WRAPPER_CLASS && !this.didChangeFrequency && !this.didSwapClubs) {
      this.showHide(wrapperEl, false);
    } else if (price > 0) {
      this.showHide(wrapperEl, true);
    } else {
      this.showHide(wrapperEl, false);
    }
  }

  formatDiscount(billingMonths, deliveryMonths, price, compare) {
    const comparePrice = compare * (billingMonths / deliveryMonths);
    const discount = ((comparePrice - price) / comparePrice) * 100;

    return `(${discount.toFixed(0)}% off)`;
  }

  /**
   * Udpdates the discount percentages displayed basedo on the current selected variant
   */
  updateDiscounts() {
    console.log('UPDATE DISCOUNTS');
    console.log(this.dataset);

    this.querySelectorAll(`.token-discount-biannually`).forEach((el) => {
      el.textContent = this.formatDiscount(
        6,
        FREQUENCY_MONTHS[this.selectedDeliveryFrequency],
        this.getPrice('biannually', this.selectedDeliveryFrequency, this.selectedVariantIdForSwap),
        this.dataset[`compare-${this.selectedVariantIdForSwap}`]
      );
    });

    this.querySelectorAll(`.token-discount-annually`).forEach((el) => {
      el.textContent = this.formatDiscount(
        12,
        FREQUENCY_MONTHS[this.selectedDeliveryFrequency],
        this.getPrice('annually', this.selectedDeliveryFrequency, this.selectedVariantIdForSwap),
        this.dataset[`compare-${this.selectedVariantIdForSwap}`]
      );
    });
  }

  /**
   * Toggles the selection of the new club to the selected variant ID
   */
  toggleNewClubSelections() {
    this.querySelectorAll(`input[name="${NEW_CLUB_INPUT_ID}"]`).forEach((el) => {
      el.checked = el.value === this.selectedVariantIdForSwap;
    });
  }

  /**
   * Toggles display of the frequency change message
   * @param {boolean} show
   */
  toggleFrequencyChangeMessage(show) {
    const el = this.querySelector(`.${BILLING_DATE_MESSAGE_CLASS}`);
    this.updateBillingDateMessage(this.selectedSubscriptionToModify);
    this.showHide(el, show);
  }

  /**
   * Toggles display of the club swap message
   * @param {boolean} show
   */
  toggleClubSwapMessage(show) {
    const el = this.querySelector(`.${DELIVERY_DATE_MESSAGE_CLASS}`);
    this.showHide(el, show);
  }

  /**
   * Toggles the display of an error message
   * @param {boolean} show
   * @param {string} message
   */
  toggleErrorMessage(show, message = DEFAULT_ERROR_MESSAGE) {
    const el = this.querySelector(`.${ERROR_MESSAGE_CLASS}`);
    const messageEl = el.querySelector('p.font-body-3');
    this.showHide(el, show);
    if (show) {
      messageEl.innerHTML = message;
    } else {
      messageEl.innerHTML = DEFAULT_ERROR_MESSAGE;
    }
  }

  /**
   * Toggles the display of a success message
   * @param {boolean} show
   */
  toggleSuccessMessage(show) {
    const el = this.querySelector(`.${SUCCESS_MESSAGE_CLASS}`);
    this.showHide(el, show);
  }

  /**
   * Toggles display of the submit button based on the component's state
   */
  toggleSubmitButton() {
    const submitButton = this.querySelector(`#${SUBMIT_BUTTON_ID}`);
    const selectedDeliveryEl = this.querySelector(
      `input[name="${DELIVERY_FREQUENCY_ID}"][value="${this.selectedDeliveryFrequency}"]`
    );
    const selectedBillingEl = this.querySelector(
      `input[name="${BILLING_FREQUENCY_ID}"][value="${this.selectedBillingFrequency}"]`
    );

    if (selectedDeliveryEl.disabled || selectedBillingEl.disabled) {
      this.toggleFrequencyChangeMessage(false);
      this.toggleClubSwapMessage(false);
      this.toggleErrorMessage(
        true,
        'Unfortunately, we cannot update your subscription with the selected options. Please make a different selection.'
      );
      submitButton.disabled = true;
      return;
    }
    this.toggleErrorMessage(false);
    submitButton.disabled = !this.didSwapClubs && !this.didChangeFrequency;
  }

  /**
   * Toggles the display of billing plans based on the component's state and available plans in Shopify
   */
  toggleUnavailablePlans() {
    const billingFrequencyInputs = this.querySelectorAll(`input[name="${BILLING_FREQUENCY_ID}"]`);

    const monthlyBillingEls = this.querySelectorAll(
      `#${BILLING_FREQUENCY_IDS.monthly}, #${BILLING_FREQUENCY_IDS.monthly} + label`
    );
    const quarterlyBillingEls = this.querySelectorAll(
      `#${BILLING_FREQUENCY_IDS.quarterly}, #${BILLING_FREQUENCY_IDS.quarterly} + label`
    );

    if (this.selectedDeliveryFrequency === 'monthly') {
      monthlyBillingEls.forEach((el) => this.showHide(el, true));
      quarterlyBillingEls.forEach((el) => this.showHide(el, false));
    } else {
      monthlyBillingEls.forEach((el) => this.showHide(el, false));
      quarterlyBillingEls.forEach((el) => this.showHide(el, true));
    }

    const disabledBillingIds = [];
    const currentSubscription = this.selectedSubscriptionToModify;
    if (currentSubscription) {
      const currentBillingFrequency = this.getFrequencyForSubscription(currentSubscription, 'billing');
      if (currentBillingFrequency in DISABLED_BILLING_FREQUENCY_IDS) {
        disabledBillingIds.push(...DISABLED_BILLING_FREQUENCY_IDS[currentBillingFrequency]);
      }
    }

    billingFrequencyInputs.forEach((billingEl) => {
      const billingUnavailable =
        this.getPrice(billingEl.value, this.selectedDeliveryFrequency, this.selectedVariantIdForSwap) < 0;

      billingEl.disabled = billingUnavailable || disabledBillingIds.includes(billingEl.id);
    });
  }

  /**
   * Builds input and label elements to render the subscription selector
   * @param {object} subscription The subscription contract to be rendered
   * @returns The input and label elements for the subscription contract
   */
  buildClubElements(subscription) {
    const inputEl = document.createElement('input');
    const contractId = subscription.subscriptionContractId;
    const contractDetails = JSON.parse(subscription.contractDetailsJSON)[0];
    const isGift = contractDetails.sellingPlanName.includes('Gift Subscription');
    inputEl.type = 'radio';
    inputEl.id = `${OLD_SUBSCRIPTION_INPUT_ID}--${contractId}`;
    inputEl.value = contractId;
    inputEl.name = OLD_SUBSCRIPTION_INPUT_ID;
    inputEl.disabled = isGift;

    const labelEl = document.createElement('label');
    labelEl.htmlFor = inputEl.id;

    const labelDiv = document.createElement('div');
    labelDiv.classList.add('brand-button-hover');
    const labelImg = document.createElement('img');
    labelImg.src = contractDetails.variantImage;
    labelImg.alt = contractDetails.title;
    labelImg.classList.add('club_image');
    labelDiv.appendChild(labelImg);

    const labelTextDiv = document.createElement('div');
    labelTextDiv.classList.add('club_text');
    const labelP = document.createElement('p');
    const labelB = document.createElement('b');
    labelB.textContent = contractDetails.title;
    labelP.classList.add('font-body-1');
    labelP.appendChild(labelB);
    labelTextDiv.appendChild(labelP);

    const labelDetails = document.createElement('p');
    labelDetails.classList.add('font-body-3');
    labelDetails.textContent = isGift
      ? 'You cannot modify a gift subscription.'
      : `${FREQUENCY_NAMES[this.getFrequencyForSubscription(subscription, 'delivery')]} Delivery • ${
          FREQUENCY_NAMES[this.getFrequencyForSubscription(subscription, 'billing')]
        } Billing`;
    labelTextDiv.appendChild(labelDetails);
    labelDiv.appendChild(labelTextDiv);
    labelEl.appendChild(labelDiv);

    return { inputEl, labelEl };
  }

  showHide(el, show) {
    if (show) {
      el.classList.remove('hidden');
    } else {
      el.classList.add('hidden');
    }
  }
}

if (!customElements.get(NS)) {
  customElements.define(NS, SubscriptionSwap);
}
